import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Nav,
  NavItem,
  Input,
  CardFooter,
  Button,
  Col,
} from "reactstrap";
import axios from "axios";
import Plot from "react-plotly.js";
import {
  baseUrl,
  last_week,
  last_month,
  last_year,
  last_8_hours,
  last_16_hours,
  last_24_hours,
} from "constants/url";
import authHeader from "services/auth-header";
import moment from "moment";
import AuditDataModal from "../../AuditData/AuditDataModal";
import AlarmInfoModal from "../../AlarmsInfo/AlarmInfoModal";

const initialChartState = {
  speed: { x: [], y: [] },
  alarmData: { x: [], y: [], group: [] },
  nextMachine: { x: [], y: [] },
};

function SpeedGraph({ title, filtersData, clientId, machineId }) {
  const [chartData, setChartData] = useState(initialChartState);
  const [durationInHours, setDurationInHours] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [duration, setDuration] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openModalAlarm, setOpenModalAlarm] = useState(false);
  const [aggregationInterval, setAggregationInterval] = useState("minute");

  const fetchData = async (filterObject) => {
    try {
      setChartData(initialChartState);
      setIsLoading(true);
      const startDate = new Date(filterObject.start_date);
      const endDate = new Date(filterObject.end_date);
      const durationInMilliseconds = endDate - startDate;
      const durationInHrs = durationInMilliseconds / (1000 * 60 * 60);
      setDurationInHours(durationInHrs);

      let dataObj = {};

      const speedData = await axios.get(
        `${baseUrl}/api/prod_chart_data/speed_per_min`,
        {
          params: { ...filterObject, clientId, machineId },
          headers: authHeader(),
        }
      );

      dataObj.speed = speedData.data.data.speed || {};

      if (durationInHrs <= 24) {
        const alarmData = await axios.get(`${baseUrl}/api/alarm_chart_data`, {
          params: { ...filterObject, clientId, machineId },
          headers: authHeader(),
        });

        dataObj.nextMachine = speedData.data.data.nextMachine || {};
        dataObj.alarmData = alarmData.data.data.alarm || {};
      }

      setAggregationInterval(speedData.data.aggregationInterval);

      setChartData({
        ...chartData,
        ...dataObj,
      });

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const onChange = (event) => {
    const value = event.target.value;
    setDuration(value);
    if (value === "") {
      fetchData(filtersData);
    } else if (value === last_week) {
      const start = moment().subtract(1, "weeks").utcOffset(0, true).format();
      const end = moment().utcOffset(0, true).format();
      fetchData({
        ...filtersData,
        start_date: start,
        end_date: end,
      });
    } else if (value === last_month) {
      const start = moment().subtract(1, "months").utcOffset(0, true).format();
      const end = moment().utcOffset(0, true).format();
      fetchData({
        ...filtersData,
        start_date: start,
        end_date: end,
      });
    } else if (value === last_year) {
      const start = moment().subtract(1, "years").utcOffset(0, true).format();
      const end = moment().utcOffset(0, true).format();
      fetchData({
        ...filtersData,
        start_date: start,
        end_date: end,
      });
    } else if (value === last_24_hours) {
      const start = moment().subtract(1, "days").utcOffset(0, true).format();
      const end = moment().utcOffset(0, true).format();
      fetchData({
        ...filtersData,
        start_date: start,
        end_date: end,
      });
    } else if (value === last_16_hours) {
      const start = moment().subtract(16, "hours").utcOffset(0, true).format();
      const end = moment().utcOffset(0, true).format();
      fetchData({
        ...filtersData,
        start_date: start,
        end_date: end,
      });
    } else if (value === last_8_hours) {
      const start = moment().subtract(8, "hours").utcOffset(0, true).format();
      const end = moment().utcOffset(0, true).format();
      fetchData({
        ...filtersData,
        start_date: start,
        end_date: end,
      });
    }
  };

  useEffect(() => {
    setDuration("");
    fetchData(filtersData);
  }, [filtersData]);

  let colorArray =
    chartData &&
    chartData.alarmData &&
    chartData.alarmData.group &&
    chartData.alarmData.group.map((item) => {
      if (item.includes("Safety")) {
        return "#d20000";
      } else if (item.includes("Infeed")) {
        return "#cea423";
      } else if (item.includes("Transfer")) {
        return "#ccce16";
      } else if (item.includes("Film")) {
        return "#db6f15";
      } else if (item.includes("Jaws")) {
        return "#9c0000";
      } else if (item.includes("Seal")) {
        return "#fdcf58";
      } else if (item.includes("Outfeed")) {
        return "#757676";
      } else if (item.includes("Temp")) {
        return "#f27d0c";
      } else if (item.includes("External")) {
        return "#800909";
      } else {
        return "#f07f13";
      }
    });

  function getSymbols(yValues) {
    // Define a mapping from y values to symbols
    const symbolMap = {
      0: "triangle-down", // Example: If y is 0, use a circle symbol
      // Using a conditional operator to select the symbol based on y value
      greaterThanZero: "triangle-up", // Example: If y is greater than 0, use a triangle-up symbol
      // Add more mappings as needed
    };

    // Create an array of symbols based on the y values
    const symbols = yValues.map((y) => {
      // Select the appropriate symbol based on the value of y
      return y > 0 ? symbolMap.greaterThanZero : symbolMap[0];
    });

    return symbols;
  }

  function getColors(yValues) {
    // Define a mapping from y values to colors
    const colorMap = {
      0: "#7f7f00", // Example: If y is 0, use a circle symbol - red
      // Using a conditional operator to select the color based on y value
      greaterThanZero: "#FFFF00", // Example: If y is greater than 0, use a different color
      // Add more mappings as needed
    };

    // Create an array of colors based on the y values
    const colors = yValues.map((y) => {
      // Select the appropriate color based on the value of y
      return y > 0 ? colorMap.greaterThanZero : colorMap[0];
    });

    return colors;
  }

  const speed = {
    type: "scatter",
    mode: "lines+points",
    x: chartData.speed.x,
    y: chartData && chartData.speed.y,
    marker: { color: "white" },
    name: `Packs/${
      aggregationInterval === "as-is" ? "minute" : aggregationInterval
    }`,
    connectgaps: true, // Connect all points
  };

  const alarm = {
    type: "scatter",
    mode: "markers",
    x: chartData.alarmData.x,
    y: chartData && chartData.alarmData.y,
    yaxis: "y2",
    // customdata: chartData && chartData.alarmDescription,
    hovertemplate: "<b>%{text}</b>",
    text: chartData && chartData.alarmData.group,
    // text: ['a','b','c','d'],
    // hovertemplate: '%{customdata}',
    marker: { color: colorArray, size: 10, symbol: "cross" },
    name: "Alarm",
  };

  const nextMachine = {
    type: "scatter",
    mode: "markers",
    x: chartData.nextMachine.x,
    y: chartData && chartData.nextMachine.y,
    // marker: { color: "blue", size: 7, symbol: "circle" },
    marker: {
      color:
        setChartData &&
        chartData.nextMachine.y &&
        getColors(chartData.nextMachine.y),
      size: 13,
      symbol:
        chartData &&
        chartData.nextMachine.y &&
        getSymbols(chartData.nextMachine.y),
    },
    name: "Next Machine",
  };

  const formattedChartData =
    durationInHours <= 24 ? [speed, alarm, nextMachine] : [speed];

  // const getDayChangeAnnotations = (timestamps) => {
  //   const annotations = [];

  //   for (let i = 1; i < timestamps.length; i++) {
  //     const prevTimestamp = timestamps[i - 1];
  //     const currTimestamp = timestamps[i];

  //     // Extract date parts directly from strings
  //     const prevDate = prevTimestamp.split("T")[0]; // "2025-03-26"
  //     const currDate = currTimestamp.split("T")[0]; // "2025-03-27"

  //     // If the date part is different, add a day change annotation
  //     if (prevDate !== currDate) {
  //       const currDateParts = currDate.split("-");
  //       const month = parseInt(currDateParts[1]);
  //       const day = parseInt(currDateParts[2]);

  //       annotations.push({
  //         x: i - 0.5, // Position between the two points
  //         y: 1,
  //         yref: "paper",
  //         yanchor: "top",
  //         showarrow: true,
  //         arrowhead: 0,
  //         arrowsize: 1,
  //         arrowwidth: 1,
  //         arrowcolor: "#ff9800",
  //         ax: 0,
  //         ay: -20,
  //         text: `Day Change: ${month}/${day}`,
  //         font: {
  //           color: "#ff9800",
  //           size: 12,
  //         },
  //       });
  //     }
  //   }

  //   return annotations;
  // };

  const options2 = {
    showlegend: true,
    autosize: true,
    legend: {
      orientation: "h",
      yanchor: "bottom",
      y: 1.02,
      xanchor: "right",
      x: 1,
    },
    yaxis: {
      title: `Packs per ${
        aggregationInterval === "as-is" ? "minute" : aggregationInterval
      }`,
    },
    yaxis2: {
      title: "Alarm",
      titlefont: { color: "red" },
      tickfont: { color: "red" },
      overlaying: "y",
      side: "right",
      range: [-1, 20],
    },
    // xaxis: {
    // type: "category", // This is the key change
    // tickformat: "%H:%M",
    // tickmode: "array",
    // ticktext: chartData.speed.x.map((date) => moment(date)),
    // ticktext: chartData.speed.x.map((date) => date.split("T")[1]),
    // tickvals: chartData.speed.x,
    // categoryarray: chartData.speed.x, // Preserve the order
    // },
    // xaxis: {
    //   type: "category", // Use category for equal spacing
    //   tickangle: -45,
    //   // Format labels by directly parsing the ISO string
    //   ticktext: chartData.speed.x.map((timestamp) => {
    //     // Parse ISO timestamp: "2025-03-26T06:00:00Z"
    //     const parts = timestamp.split("T"); // Split at T: ["2025-03-26", "06:00:00Z"]
    //     const datePart = parts[0]; // "2025-03-26"
    //     const timePart = parts[1].replace("Z", ""); // "06:00:00"

    //     // Split date: ["2025", "03", "26"]
    //     const dateComponents = datePart.split("-");
    //     const month = parseInt(dateComponents[1]);
    //     const day = parseInt(dateComponents[2]);

    //     // Split time: ["06", "00", "00"]
    //     const timeComponents = timePart.split(":");
    //     const hours = timeComponents[0];
    //     const minutes = timeComponents[1];

    //     // Format as "MM/DD HH:MM"
    //     return aggregationInterval === "as-is"
    //       ? `${hours}:${minutes}`
    //       : `${month}/${day} ${hours}:${minutes}`;
    //   }),
    //   tickvals: chartData.speed.x,
    // },

    xaxis: {
      type: "category", // Use category for equal spacing
      tickangle: -45,
      // Format labels by directly parsing the ISO string
      ticktext: chartData.speed.x.map((timestamp, index) => {
        // Parse ISO timestamp: "2025-03-26T06:00:00Z"
        const parts = timestamp.split("T"); // Split at T: ["2025-03-26", "06:00:00Z"]
        const datePart = parts[0]; // "2025-03-26"
        const timePart = parts[1].replace("Z", ""); // "06:00:00"

        // Split date: ["2025", "03", "26"]
        const dateComponents = datePart.split("-");
        const month = parseInt(dateComponents[1]);
        const day = parseInt(dateComponents[2]);

        // Split time: ["06", "00", "00"]
        const timeComponents = timePart.split(":");
        const hours = timeComponents[0];
        const minutes = timeComponents[1];

        if (aggregationInterval === "as-is") {
          // Only show hour labels at the start of each hour or every 10th point
          // if (minutes === "00" || index % 20 === 0) {
          if (minutes === "00") {
            // if (index % 15 === 0) {
            return `${hours}:${minutes}`;
          } else {
            return ""; // Return empty string for most minute labels
          }
        } else {
          return `${month}/${day} ${hours}:${minutes}`;
        }
      }),
      tickvals: chartData.speed.x,
      // Show all gridlines even when labels are hidden
      // showgrid: true,
      // gridcolor: "rgba(255,255,255,0.1)",
    },
    paper_bgcolor: "rgb(36,41,40)",
    plot_bgcolor: "rgb(36,41,40)",
    font: {
      family: "Open Sans, sans-serif",
      size: 13,
      color: "white",
    },
    // annotations: getDayChangeAnnotations(chartData.speed.x),
  };

  const config = {
    displayModeBar: false, // this is the line that hides the bar.
    // responsive: true
  };

  return (
    <>
      <Card className="shadow">
        <CardHeader className="bg-transparent">
          <Row className="align-items-center">
            <div className="col">
              <h2 className="mb-0" style={{ color: "#0ca6f2" }}>
                {/* {title} */}
                {`Packs per ${
                  aggregationInterval === "as-is"
                    ? "minute"
                    : aggregationInterval
                }`}
              </h2>
            </div>
            <div className="col">
              <Nav className="justify-content-end" pills>
                <NavItem>
                  <Input
                    placeholder="Duration"
                    name="country_id"
                    type="select"
                    value={duration}
                    className="textBg"
                    bsSize="sm"
                    style={{ color: "#000000" }}
                    onChange={onChange}
                  >
                    {[
                      "",
                      last_week,
                      last_month,
                      last_year,
                      last_8_hours,
                      last_16_hours,
                      last_24_hours,
                    ].map((option, idx) => {
                      return (
                        <option key={idx} value={option}>
                          {option}
                        </option>
                      );
                    })}
                  </Input>
                </NavItem>
              </Nav>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="m-5">Loading...</div>
          ) : Object.keys(chartData).length > 0 ? (
            <div className="chart" style={{ height: "auto" }}>
              <div className="chart-container-bar">
                {chartData && (
                  <Plot
                    data={formattedChartData}
                    className="w-100 h-100"
                    useResizeHandler
                    layout={options2}
                    config={config}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="m-5">No Data Found</div>
          )}
        </CardBody>
        <CardFooter className="bg-transparent">
          <Row className="align-items-center justify-content-center">
            <Col xs={6} className="text-right">
              <Button
                className="w-50"
                onClick={() => setOpenModal(true)}
                // disabled={!Object.keys(chartData).length > 0}
                style={{ backgroundColor: "#0ca6f2", border: "none" }}
              >
                Audit Data
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                onClick={() => setOpenModalAlarm(true)}
                // disabled={!Object.keys(chartData).length > 0}
                className="w-50"
                color="secondary"
              >
                Alarms Info
              </Button>
            </Col>
          </Row>
        </CardFooter>
      </Card>
      <AuditDataModal
        isOpen={openModal}
        toggle={() => setOpenModal((prevState) => !prevState)}
        clientId={clientId}
        machineId={machineId}
        filter={filtersData}
      />
      <AlarmInfoModal
        isOpen={openModalAlarm}
        toggle={() => setOpenModalAlarm((prevState) => !prevState)}
        clientId={clientId}
        machineId={machineId}
        filter={filtersData}
      />
    </>
  );
}

export default SpeedGraph;
